import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Topbar from '../components/Topbar'
import { useToast } from '../context/ToastContext'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'

const PURPLE = '#7C3AED'
const PURPLE_BG = '#F5F3FF'

const CUSTOMERS_PER_PAGE = 20
const WINBACK_DAYS = 45

// Same canonical buckets as AddOrder.js — keeps Age Mix chart from fragmenting
const AGE_GROUPS = ['Not specified', 'Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+']

const FLAG_REASONS = [
  { key: 'ghost_order',       label: 'Ghost Order',        desc: 'Placed order then disappeared — no payment, no response' },
  { key: 'rejected_delivery', label: 'Rejected Delivery',  desc: 'Courier arrived, customer refused to accept' },
  { key: 'fake_address',      label: 'Fake Address',       desc: 'Delivery failed — address does not exist' },
  { key: 'payment_fraud',     label: 'Payment Fraud',      desc: 'bKash/Nagad payment reversed or disputed fraudulently' },
  { key: 'customer_return',   label: 'Unjustified Return', desc: 'Returned without valid reason (not seller fault)' },
  { key: 'abusive',           label: 'Abusive Behaviour',  desc: 'Rude, threatening or abusive towards seller' },
]

const FLAG_LEVEL_CONFIG = {
  watch:     { label: 'Watch',     color: 'var(--warning)', bg: 'var(--warning-light)', border: 'var(--warning-border)' },
  caution:   { label: 'Caution',   color: '#EA580C',        bg: '#FFF7ED',              border: '#FED7AA' },
  high_risk: { label: 'High Risk', color: 'var(--error)',   bg: 'var(--error-light)',   border: 'var(--error-border)' },
}

// ── Flat icon set (20x20 viewBox, fillRule evenodd) ─────────────────────────
const ICONS = {
  users:      'M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z',
  check:      'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  badge:      'M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  diamond:    'M5.7 2.3a1 1 0 01.7-.3h7.2a1 1 0 01.7.3l3.3 3.3a1 1 0 010 1.5l-6.9 8.5a1 1 0 01-1.4 0L2.4 7.1a1 1 0 010-1.5l3.3-3.3z',
  refresh:    'M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0v2.43l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z',
  plus:       'M10 2a1 1 0 011 1v6h6a1 1 0 110 2h-6v6a1 1 0 11-2 0v-6H3a1 1 0 110-2h6V3a1 1 0 011-1z',
  flag:       'M3 3a1 1 0 00-1 1v13a1 1 0 102 0v-3.382l1.447-.724A4 4 0 018.764 13l.894.447a4 4 0 003.788 0L17 11.882V4.118l-3.553 1.776a2 2 0 01-1.789 0L9.764 4.67a4 4 0 00-3.528 0L4 5.618V4a1 1 0 00-1-1z',
  star:       'M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z',
  phone:      'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  chat:       'M2 10c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9.06 9.06 0 01-2.857-.45c-.832.74-1.917 1.45-2.943 1.45a.6.6 0 01-.55-.84c.31-.71.51-1.49.557-2.272A6.96 6.96 0 012 10z',
  external:   'M11 3a1 1 0 100 2h2.586L7.293 11.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5zM5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-2a1 1 0 10-2 0v2H5V7h2a1 1 0 100-2H5z',
  mail:       'M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884zM18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z',
  gift:       'M5 8a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4zM3 9a1 1 0 011-1h12a1 1 0 011 1v1a2 2 0 01-1 1.732V16a1 1 0 01-1 1H5a1 1 0 01-1-1v-4.268A2 2 0 013 10V9z',
  clock:      'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z',
  trending:   'M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z',
  user:       'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z',
  trash:      'M9 2a1 1 0 00-1 1v1H4.5a.5.5 0 000 1H5v11a2 2 0 002 2h6a2 2 0 002-2V5h.5a.5.5 0 000-1H12V3a1 1 0 00-1-1H9zM8 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z',
  search:     'M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z',
  download:   'M10 2a1 1 0 011 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 10.586V3a1 1 0 011-1zM3 16a1 1 0 011 1v1h12v-1a1 1 0 112 0v1a2 2 0 01-2 2H4a2 2 0 01-2-2v-1a1 1 0 011-1z',
  filter:     'M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 01.628.74v2.288a2.25 2.25 0 01-.659 1.59l-4.682 4.683a2.25 2.25 0 00-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 018 18.25v-5.757a2.25 2.25 0 00-.659-1.591L2.659 6.22A2.25 2.25 0 012 4.629V2.34a.75.75 0 01.628-.74z',
  columns:    'M3 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM9 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4zM15 4a1 1 0 011-1h1a1 1 0 011 1v12a1 1 0 01-1 1h-1a1 1 0 01-1-1V4z',
  chevronDown:'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z',
}

function FlatIcon({ name, size = 14, color = 'var(--text-muted)' }) {
  const d = ICONS[name]
  if (!d) return null
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color} style={{ flexShrink: 0 }}>
      <path fillRule="evenodd" d={d} clipRule="evenodd" />
    </svg>
  )
}

function getFlag(countryCode) {
  if (!countryCode) return ''
  const clean = countryCode.replace('+', '')
  const map = { '880':'🇧🇩','91':'🇮🇳','92':'🇵🇰','94':'🇱🇰','977':'🇳🇵','61':'🇦🇺','44':'🇬🇧','1':'🇺🇸','971':'🇦🇪','966':'🇸🇦','60':'🇲🇾','65':'🇸🇬','62':'🇮🇩','66':'🇹🇭','81':'🇯🇵','82':'🇰🇷','86':'🇨🇳','49':'🇩🇪','33':'🇫🇷','39':'🇮🇹','34':'🇪🇸','7':'🇷🇺','55':'🇧🇷','27':'🇿🇦','20':'🇪🇬','234':'🇳🇬','254':'🇰🇪','64':'🇳🇿','31':'🇳🇱','46':'🇸🇪' }
  return map[clean] || '🌍'
}

function formatFullAddress(c) {
  return [c.house, c.road, c.neighbourhood || c.moholla, c.metro_thana || c.thana, c.district, c.division, c.zip, c.country].filter(p => p && p !== 'N/A').join(', ') || '—'
}

// Remaining address details once Country/Division/District have their own columns
function formatLocationRemaining(c) {
  return [c.house, c.road, c.neighbourhood || c.moholla, c.metro_thana || c.thana, c.zip].filter(p => p && p !== 'N/A').join(', ') || '—'
}

// ── Generic "special date" status — used for both birthdays and anniversaries ──
function getDateStatus(dateStr, t) {
  if (!dateStr) return null
  const today = new Date()
  const d = new Date(dateStr)
  const thisYear = new Date(today.getFullYear(), d.getMonth(), d.getDate())
  const diff = Math.floor((thisYear - today) / 86400000)
  if (diff === 0) return { label: t('crm.today'), urgent: true, days: 0 }
  if (diff === 1) return { label: t('crm.tomorrow'), urgent: true, days: 1 }
  if (diff >= 2 && diff <= 7) return { label: t('crm.in_days') + ' ' + diff + ' ' + t('crm.days'), urgent: false, days: diff }
  if (diff < 0) {
    const nextYear = new Date(today.getFullYear() + 1, d.getMonth(), d.getDate())
    const diffNext = Math.floor((nextYear - today) / 86400000)
    if (diffNext <= 7) return { label: t('crm.in_days') + ' ' + diffNext + ' ' + t('crm.days'), urgent: false, days: diffNext }
  }
  return null
}

function formatDateLong(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-BD', { day: 'numeric', month: 'long' })
}

// ── Win-back: 2+ orders, no order in WINBACK_DAYS+, not contacted since last order ──
function getWinbackStatus(customer, statsMap) {
  const s = statsMap[customer.id]
  if (!s || s.total_orders < 2 || !s.last_order) return null
  const daysSince = Math.floor((Date.now() - new Date(s.last_order)) / 86400000)
  if (daysSince < WINBACK_DAYS) return null
  if (customer.winback_contacted_at && new Date(customer.winback_contacted_at) > new Date(s.last_order)) return null
  return { daysSince }
}

function getTier(customerId, statsMap, t) {
  const s   = statsMap[customerId] || { total_orders: 0, total_spend: 0 }
  const all = Object.values(statsMap).map(x => x.total_spend)
  const avg = all.length > 0 ? all.reduce((a, b) => a + b, 0) / all.length : 0
  if (s.total_orders >= 5 || s.total_spend > avg * 2)
    return { key: 'loyal',      label: t('crm.tier_loyal'),      color: 'var(--success)', bg: 'var(--success-light)', icon: 'badge' }
  if (s.total_spend > avg * 1.5)
    return { key: 'high_value', label: t('crm.tier_high_value'), color: 'var(--primary)', bg: 'var(--primary-light)', icon: 'diamond' }
  if (s.total_orders >= 2)
    return { key: 'returning',  label: t('crm.tier_returning'),  color: PURPLE,            bg: PURPLE_BG,              icon: 'refresh' }
  if (s.total_orders === 1)
    return { key: 'active',     label: t('crm.tier_active'),     color: 'var(--success)', bg: 'var(--success-light)', icon: 'check' }
  return   { key: 'no_orders',  label: t('crm.tier_no_orders'),  color: 'var(--text-muted)', bg: 'var(--bg-muted)',    icon: 'user' }
}

function Avatar({ name, size = 36, light = false }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
  const palette  = ['#0EA5E9','#16A34A','#7C3AED','#D97706','#0F766E','#DC2626','#0369A1','#9333EA']
  const color    = palette[(name?.charCodeAt(0) || 0) % palette.length]
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: light ? 'rgba(255,255,255,0.2)' : color + '18', border: '1.5px solid ' + (light ? 'rgba(255,255,255,0.4)' : color + '35'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: size * 0.36 + 'px', fontWeight: 700, color: light ? '#FFFFFF' : color, fontFamily: 'var(--font-ui)' }}>{initials}</span>
    </div>
  )
}

// ── Animated counter hook ────────────────────────────────────────────────────
function useCountUp(target, duration = 700) {
  const [val, setVal] = useState(0)
  const prev = useRef(0)
  useEffect(() => {
    if (!target) { setVal(0); return }
    const start = prev.current
    const startTime = performance.now()
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setVal(Math.round(start + (target - start) * eased))
      if (progress < 1) requestAnimationFrame(tick)
      else prev.current = target
    }
    requestAnimationFrame(tick)
  }, [target, duration])
  return val
}

// ── Animated horizontal bar (grows on mount) ─────────────────────────────────
function AnimatedBar({ pct, color = 'var(--primary)', delay = 0 }) {
  const [w, setW] = useState(0)
  useEffect(() => { const tmr = setTimeout(() => setW(pct), 80 + delay); return () => clearTimeout(tmr) }, [pct, delay])
  return (
    <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: 'var(--bg-muted)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: w + '%', background: color, borderRadius: '3px', transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1)' }} />
    </div>
  )
}

// ── Animated mini bar chart (new customers per month) ─────────────────────────
function MiniBar({ data, color = 'var(--primary)', height = 50 }) {
  const [animated, setAnimated] = useState(false)
  useEffect(() => { const tmr = setTimeout(() => setAnimated(true), 100); return () => clearTimeout(tmr) }, [])
  if (!data || data.length === 0) return null
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: (height + 20) + 'px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
          <div style={{ width: '100%', borderRadius: '3px 3px 0 0', background: color, height: (animated ? Math.max((d.value / max) * height, d.value > 0 ? 3 : 0) : 0) + 'px', minHeight: d.value > 0 ? '3px' : '0', transition: `height 0.6s cubic-bezier(0.4,0,0.2,1) ${i * 0.05}s` }} />
          <div style={{ fontSize: '9px', color: 'var(--text-muted)', whiteSpace: 'nowrap', textAlign: 'center', fontFamily: 'var(--font-body)' }}>{d.label}</div>
        </div>
      ))}
    </div>
  )
}

// ── Lifetime value sparkline — last 6 months spend, from a customer's orders ──
function Sparkline({ orders, t }) {
  const months = []
  for (let m = 5; m >= 0; m--) {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - m)
    months.push({ label: d.toLocaleString('default', { month: 'short' }), y: d.getFullYear(), mo: d.getMonth(), value: 0 })
  }
  ;(orders || []).forEach(o => {
    if (o.status === 'return') return
    const d = new Date(o.created_at)
    const slot = months.find(mm => mm.y === d.getFullYear() && mm.mo === d.getMonth())
    if (slot) slot.value += Number(o.total_amount || 0)
  })
  const max = Math.max(...months.map(m => m.value), 1)
  const total = months.reduce((s, m) => s + m.value, 0)

  if (total === 0) return null

  const W = 280, H = 56, pad = 4
  const step = (W - pad * 2) / (months.length - 1)
  const points = months.map((m, i) => {
    const x = pad + i * step
    const y = H - pad - (m.value / max) * (H - pad * 2)
    return [x, y]
  })
  const pathD = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0] + ' ' + p[1]).join(' ')
  const areaD = pathD + ` L ${points[points.length-1][0]} ${H} L ${points[0][0]} ${H} Z`

  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px', fontFamily: 'var(--font-ui)' }}>{t('crm.ltv_title')}</div>
      <div style={{ padding: '10px 12px', borderRadius: '10px', background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
          <defs>
            <linearGradient id="ltv-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.25" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#ltv-grad)" />
          <path d={pathD} fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {points.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="var(--primary)" />)}
        </svg>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          {months.map((m, i) => <span key={i} style={{ fontSize: '9.5px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>{m.label}</span>)}
        </div>
      </div>
    </div>
  )
}

const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: '9px', padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 0.15s' }

function Inp({ type = 'text', value, onChange, placeholder, label, optional }) {
  return (
    <div>
      {label && <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui)' }}>{label}{optional && <span style={{ fontWeight: 400, color: 'var(--text-muted)', opacity: 0.7, marginLeft: '4px' }}>optional</span>}</label>}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...inp }}
        onFocus={e => e.target.style.borderColor = 'var(--primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
    </div>
  )
}

const STATUS_COLORS = {
  received: { bg: 'var(--primary-light)', text: 'var(--primary)' }, pending: { bg: 'var(--warning-light)', text: 'var(--warning)' },
  confirmed: { bg: '#FEF3C7', text: '#92400E' }, processing: { bg: 'var(--success-light)', text: 'var(--success)' },
  with_courier: { bg: PURPLE_BG, text: PURPLE }, shipped: { bg: 'var(--success-light)', text: 'var(--success)' },
  closed: { bg: 'var(--success-light)', text: 'var(--success)' }, return: { bg: 'var(--error-light)', text: 'var(--error)' },
}

// ── Quick contact row — Call / WhatsApp / Messenger / New order ─────────────
function QuickContact({ customer, t, onNewOrder }) {
  const items = []
  if (customer.phone) items.push({ key: 'call', icon: 'phone', label: t('crm.quick_call'), href: 'tel:' + customer.phone, color: 'var(--success)' })
  if (customer.whatsapp) items.push({ key: 'wa', icon: 'chat', label: t('crm.quick_whatsapp'), href: 'https://wa.me/' + customer.whatsapp.replace(/[^0-9]/g, ''), color: '#16A34A' })
  if (customer.facebook_url) items.push({ key: 'fb', icon: 'external', label: t('crm.quick_messenger'), href: customer.facebook_url, color: 'var(--primary)' })

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length + 1}, 1fr)`, gap: '8px', marginBottom: '14px' }}>
      {items.map(it => (
        <a key={it.key} href={it.href} target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: '10px 6px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-card)', textDecoration: 'none', fontFamily: 'var(--font-ui)', transition: 'var(--transition-fast)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = it.color; e.currentTarget.style.background = it.color + '0d' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)' }}>
          <FlatIcon name={it.icon} size={16} color={it.color} />
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)' }}>{it.label}</span>
        </a>
      ))}
      <button onClick={onNewOrder}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: '10px 6px', borderRadius: '10px', border: 'none', background: 'var(--primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
        <FlatIcon name="plus" size={16} color="#FFFFFF" />
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#FFFFFF' }}>{t('crm.new_order')}</span>
      </button>
    </div>
  )
}

function SegmentGuide({ t }) {
  const [open, setOpen] = useState(false)
  const SEGMENT_GUIDE = [
    { icon: 'badge',    label: t('crm.tier_loyal'),      color: 'var(--success)', desc: t('crm.seg_loyal_desc') },
    { icon: 'diamond',  label: t('crm.tier_high_value'), color: 'var(--primary)', desc: t('crm.seg_high_value_desc') },
    { icon: 'refresh',  label: t('crm.tier_returning'),  color: PURPLE,           desc: t('crm.seg_returning_desc') },
    { icon: 'check',    label: t('crm.tier_active'),     color: 'var(--success)', desc: t('crm.seg_active_desc') },
    { icon: 'user',     label: t('crm.tier_no_orders'),  color: 'var(--text-muted)', desc: t('crm.seg_no_orders_desc') },
  ]
  return (
    <div style={{ marginBottom: '12px' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
        {t('crm.segment_guide_btn')}
        <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
      </button>
      {open && (
        <div style={{ marginTop: '8px', background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {SEGMENT_GUIDE.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <FlatIcon name={s.icon} size={15} color={s.color} />
              <div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: s.color, fontFamily: 'var(--font-ui)' }}>{s.label}</span>
                <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginLeft: '6px', fontFamily: 'var(--font-body)' }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Flag Modal ──────────────────────────────────────────────────────────────
function FlagModal({ customerName, phone, existingFlag, onFlag, onUnflag, onClose }) {
  const [reason, setReason]     = useState('')
  const [detail, setDetail]     = useState('')
  const [saving, setSaving]     = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const handleSubmit = async () => {
    if (!reason || !confirmed) return
    setSaving(true)
    await onFlag({ reasonCategory: reason, reasonDetail: detail })
    setSaving(false)
  }

  const handleUnflag = async () => {
    setSaving(true)
    await onUnflag()
    setSaving(false)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '460px', fontFamily: 'var(--font-body)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '16px 20px', background: existingFlag ? 'var(--bg-muted)' : 'var(--error-light)', borderBottom: '1px solid ' + (existingFlag ? 'var(--border)' : 'var(--error-border)'), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: existingFlag ? 'var(--text-primary)' : 'var(--error)', fontFamily: 'var(--font-ui)' }}>{existingFlag ? 'Manage Flag' : 'Flag Customer'}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{customerName} · {phone}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {existingFlag ? (
            <>
              <div style={{ background: 'var(--error-light)', border: '1px solid var(--error-border)', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--error)', marginBottom: '4px', fontFamily: 'var(--font-ui)' }}>Currently Flagged</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Reason: <strong style={{ color: 'var(--text-primary)' }}>{existingFlag.reason_category?.replace(/_/g, ' ')}</strong></div>
                {existingFlag.reason_detail && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>Note: {existingFlag.reason_detail}</div>}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Flagged on {new Date(existingFlag.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '16px' }}>
                If this customer has resolved the issue or you believe the flag was set in error, you can remove it. Removing your flag does not affect community flags from other sellers.
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>Cancel</button>
                <button onClick={handleUnflag} disabled={saving}
                  style={{ flex: 1, padding: '11px', borderRadius: '10px', background: saving ? 'var(--border)' : 'var(--success)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#FFFFFF', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)' }}>
                  {saving ? 'Removing...' : 'Remove Flag'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ background: 'var(--warning-light)', border: '1px solid var(--warning-border)', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#92400E', lineHeight: 1.6 }}>
                <strong>Important:</strong> Your flag is private — only you can see it. It only becomes a community warning when 3+ different sellers independently flag the same number.
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>Reason *</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {FLAG_REASONS.map(r => (
                    <button key={r.key} onClick={() => setReason(r.key)}
                      style={{ padding: '10px 14px', borderRadius: '10px', border: `2px solid ${reason === r.key ? 'var(--error)' : 'var(--border)'}`, background: reason === r.key ? 'var(--error-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-body)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: reason === r.key ? 'var(--error)' : 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-ui)' }}>{r.label}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{r.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '5px', fontFamily: 'var(--font-ui)' }}>Additional details <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></div>
                <textarea value={detail} onChange={e => setDetail(e.target.value)} placeholder="Any extra details..." rows={2}
                  style={{ ...inp, resize: 'none', background: 'var(--bg-muted)' }}
                  onFocus={e => e.target.style.borderColor = 'var(--error)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px', padding: '12px', background: 'var(--bg-muted)', borderRadius: '9px' }}>
                <button onClick={() => setConfirmed(!confirmed)}
                  style={{ width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${confirmed ? 'var(--error)' : 'var(--border)'}`, background: confirmed ? 'var(--error)' : 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginTop: '1px' }}>
                  {confirmed && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  I confirm this flag is based on genuine experience. I understand misuse of the flagging system may result in account review.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>Cancel</button>
                <button onClick={handleSubmit} disabled={!reason || !confirmed || saving}
                  style={{ flex: 1, padding: '11px', borderRadius: '10px', background: reason && confirmed && !saving ? 'var(--error)' : 'var(--border)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#FFFFFF', cursor: reason && confirmed && !saving ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-ui)' }}>
                  {saving ? 'Flagging...' : 'Submit Flag'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Table column configuration ───────────────────────────────────────────
const ALL_COLUMNS = [
  { key: 'sl',         label: 'col_sl',         width: 36,  locked: true },
  { key: 'customer',   label: 'col_customer',   width: 220, locked: true },
  { key: 'phone',      label: 'col_phone',      width: 120 },
  { key: 'segment',    label: 'col_segment',    width: 130, sortable: true },
  { key: 'district',   label: 'col_district',   width: 110, sortable: true },
  { key: 'location',   label: 'col_location',   width: 170 },
  { key: 'orders',     label: 'col_orders',     width: 70,  sortable: true },
  { key: 'spend',      label: 'col_spend',      width: 100, sortable: true },
  { key: 'last_order', label: 'col_last_order', width: 110, sortable: true },
  { key: 'flags',      label: 'col_flags',      width: 110 },
  { key: 'actions',    label: 'col_actions',    width: 120, locked: true },
  { key: 'country',    label: 'col_country',    width: 110, sortable: true },
  { key: 'division',   label: 'col_division',   width: 110, sortable: true },
  { key: 'gender',     label: 'col_gender',     width: 90 },
  { key: 'age_range',  label: 'col_age_group',  width: 100 },
  { key: 'added',      label: 'col_added',      width: 100, sortable: true },
]
const DEFAULT_VISIBLE_COLS = ['sl','customer','phone','segment','district','location','orders','spend','last_order','flags','actions']
const COLUMNS_STORAGE_KEY = 'pc_crm_columns_v1'

// ── Columns visibility menu ───────────────────────────────────────────────
function ColumnsMenu({ visible, onChange, t }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const toggleCol = (key) => onChange(visible.includes(key) ? visible.filter(k => k !== key) : [...visible, key])
  return (
    <div ref={ref} style={{ position: 'relative' }} className="crm-columns-btn">
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '9px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
        <FlatIcon name="columns" size={13} />
        {t('crm.columns_btn')}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: 'var(--shadow-md)', padding: '8px', zIndex: 60, minWidth: '180px', maxHeight: '320px', overflowY: 'auto' }}>
          {ALL_COLUMNS.filter(c => !c.locked).map(c => (
            <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <input type="checkbox" checked={visible.includes(c.key)} onChange={() => toggleCol(c.key)} style={{ accentColor: 'var(--primary)', width: '14px', height: '14px' }} />
              {t('crm.' + c.label)}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Multi-select dropdown filter (Country / Division / Gender / Age etc.) ──
function MultiSelectFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  if (options.length === 0) return null
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v])
  const active = selected.length > 0
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '20px', border: '1px solid', borderColor: active ? 'var(--primary)' : 'var(--border)', background: active ? 'var(--primary-light)' : 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: active ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
        {label}{active ? ` (${selected.length})` : ''}
        <FlatIcon name="chevronDown" size={10} color={active ? 'var(--primary)' : 'var(--text-muted)'} />
      </button>
      {open && (
        <div style={{ position: 'absolute', left: 0, top: 'calc(100% + 4px)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: 'var(--shadow-md)', padding: '8px', zIndex: 60, minWidth: '160px', maxHeight: '240px', overflowY: 'auto' }}>
          {options.map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} style={{ accentColor: 'var(--primary)', width: '14px', height: '14px' }} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Donut chart — proportional breakdown (segment mix, gender mix, etc.) ───
function DonutChart({ data, t, size = 110 }) {
  const [animated, setAnimated] = useState(false)
  useEffect(() => { const tm = setTimeout(() => setAnimated(true), 100); return () => clearTimeout(tm) }, [])
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px' }}>{t('crm.no_chart_data')}</div>
  const r = size / 2 - 9
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {data.filter(d => d.value > 0).map((d, i) => {
            const frac = d.value / total
            const dash = animated ? frac * c : 0
            const el = (
              <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={d.color} strokeWidth="13"
                strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-offset}
                style={{ transition: 'stroke-dasharray 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
            )
            offset += frac * c
            return el
          })}
        </g>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
        {data.filter(d => d.value > 0).map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontFamily: 'var(--font-body)' }}>
            <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: d.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'var(--font-ui)' }}>{d.label}</span>
            <span style={{ color: 'var(--text-muted)' }}>{d.value} ({Math.round(d.value / total * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CRM() {
  const { seller, isFeatureAllowed } = useAuth()
  const navigate     = useNavigate()
  const { showToast } = useToast()
  const { t }        = useTranslation()
  const currency     = seller?.seller_currency || 'BDT'
  const sym          = getCurrencySymbol(currency)
  const fmt          = useCallback((n) => {
    const abs = Math.abs(Math.round(n || 0))
    if (abs >= 1000000) return sym + (abs / 1000000).toFixed(1) + 'M'
    if (abs >= 100000)  return sym + Math.round(abs / 1000) + 'k'
    return sym + abs.toLocaleString()
  }, [sym])

  const [customers, setCustomers]         = useState([])
  const [customerStats, setCustomerStats] = useState({})
  const [customerFlags, setCustomerFlags] = useState({}) // phone -> flag obj
  const [communityFlags, setCommunityFlags] = useState({}) // phone -> community flag
  const [loading, setLoading]             = useState(true)
  const [search, setSearch]               = useState('')
  const [filter, setFilter]               = useState('all')
  const [currentPage, setCurrentPage]     = useState(1)
  const [selected, setSelected]           = useState(null)
  const [deleteId, setDeleteId]           = useState(null)
  const [deleting, setDeleting]           = useState(false)
  const [editMode, setEditMode]           = useState(false)
  const [savingEdit, setSavingEdit]       = useState(false)
  const [activeTab, setActiveTab]         = useState('info')
  const [customerNotes, setCustomerNotes] = useState([])
  const [customerOrders, setCustomerOrders] = useState([])
  const [newNote, setNewNote]             = useState('')
  const [savingNote, setSavingNote]       = useState(false)
  const [contactBy, setContactBy]         = useState('Owner')
  const [birthdayCustomers, setBirthdayCustomers] = useState([])
  const [anniversaryCustomers, setAnniversaryCustomers] = useState([])
  const [winbackCustomers, setWinbackCustomers] = useState([])
  const [bdaySettings, setBdaySettings]   = useState('')
  const [anniSettings, setAnniSettings]   = useState('')
  const [winbackSettings, setWinbackSettings] = useState('')
  const [summary, setSummary]             = useState({ total: 0, active: 0, highValue: 0, loyal: 0, newThisMonth: 0, totalRevenue: 0, favourites: 0 })
  const [monthlyData, setMonthlyData]     = useState([])
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [flagTarget, setFlagTarget]       = useState(null)
  const [isMobile, setIsMobile]           = useState(typeof window !== 'undefined' && window.innerWidth <= 768)
  const [visibleCols, setVisibleCols]     = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY)); if (Array.isArray(saved) && saved.length) return saved } catch {}
    return DEFAULT_VISIBLE_COLS
  })
  const [showFiltersPanel, setShowFiltersPanel] = useState(false)
  const [colFilters, setColFilters]       = useState({ country: [], division: [], district: [], gender: [], age_range: [] })
  const [sortCol, setSortCol]             = useState(null)
  const [sortDir, setSortDir]             = useState('asc')

  useEffect(() => { try { localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleCols)) } catch {} }, [visibleCols])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const blankEdit = (c) => ({
    name: c?.name || '', phone: c?.phone || '', email: c?.email || '',
    whatsapp: c?.whatsapp || '', facebook_url: c?.facebook_url || '',
    instagram_url: c?.instagram_url || '',
    neighbourhood: c?.neighbourhood || '', metro_thana: c?.metro_thana || '',
    thana: c?.thana || '', district: c?.district || '', division: c?.division || '',
    moholla: c?.moholla || '', house: c?.house || '', road: c?.road || '',
    zip: c?.zip || '', country: c?.country || '', tags: c?.tags || '',
    birthday: c?.birthday || '', anniversary: c?.anniversary || '',
    gender: c?.gender || '', age_range: c?.age_range || '',
    interests: c?.interests || '', hobbies: c?.hobbies || '',
    occupation: c?.occupation || '',
  })
  const [editForm, setEditForm] = useState(blankEdit(null))
  const setEF = (k, v) => setEditForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    if (seller?.id) { fetchCustomers(); fetchMessageSettings() }
  }, [seller]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMessageSettings = async () => {
    const { data } = await supabase.from('seller_invoice_settings').select('birthday_message_template, anniversary_message_template, winback_message_template').eq('seller_id', seller.id).single()
    setBdaySettings(data?.birthday_message_template || '')
    setAnniSettings(data?.anniversary_message_template || '')
    setWinbackSettings(data?.winback_message_template || '')
  }

  const fetchCustomers = async () => {
    setLoading(true)
    const { data: custData } = await supabase.from('customers').select('*').eq('seller_id', seller.id).order('created_at', { ascending: false })
    const { data: ordersData } = await supabase.from('orders').select('customer_id, total_amount, status, created_at, order_source').eq('seller_id', seller.id)

    const statsMap = {}
    ordersData?.forEach(o => {
      if (!o.customer_id) return
      if (!statsMap[o.customer_id]) statsMap[o.customer_id] = { total_orders: 0, total_spend: 0, last_order: null, source: o.order_source }
      statsMap[o.customer_id].total_orders++
      if (o.status !== 'return') statsMap[o.customer_id].total_spend += Number(o.total_amount || 0)
      const d = new Date(o.created_at)
      if (!statsMap[o.customer_id].last_order || d > new Date(statsMap[o.customer_id].last_order)) statsMap[o.customer_id].last_order = o.created_at
    })
    setCustomerStats(statsMap)

    // Fetch private flags for all phones
    const phones = (custData || []).map(c => c.phone).filter(Boolean)
    if (phones.length > 0) {
      const { data: flags } = await supabase
        .from('customer_flags')
        .select('id, phone, reason_category, reason_detail, created_at, is_active')
        .eq('flagged_by_seller_id', seller.id)
        .eq('is_active', true)
        .in('phone', phones)
      const flagMap = {}
      flags?.forEach(f => { flagMap[f.phone] = f })
      setCustomerFlags(flagMap)

      // Fetch community flags
      const { data: comFlags } = await supabase
        .from('community_flags')
        .select('*')
        .in('phone', phones)
      const comMap = {}
      comFlags?.forEach(f => { comMap[f.phone] = f })
      setCommunityFlags(comMap)
    }

    const now = new Date(); const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const allSpends = Object.values(statsMap).map(s => s.total_spend)
    const avg = allSpends.length > 0 ? allSpends.reduce((a, b) => a + b, 0) / allSpends.length : 0

    setSummary({
      total: custData?.length || 0,
      active: Object.values(statsMap).filter(s => s.total_orders > 0).length,
      highValue: Object.values(statsMap).filter(s => s.total_spend > avg * 1.5).length,
      loyal: Object.values(statsMap).filter(s => s.total_orders >= 5 || s.total_spend > avg * 2).length,
      newThisMonth: (custData || []).filter(c => new Date(c.created_at) >= startOfMonth).length,
      totalRevenue: allSpends.reduce((a, b) => a + b, 0),
      favourites: (custData || []).filter(c => c.is_favourite).length,
    })

    const months = []
    for (let m = 5; m >= 0; m--) {
      const d = new Date(); d.setMonth(d.getMonth() - m)
      months.push({ label: d.toLocaleString('default', { month: 'short' }), value: (custData || []).filter(c => { const cd = new Date(c.created_at); return cd.getMonth() === d.getMonth() && cd.getFullYear() === d.getFullYear() }).length })
    }
    setMonthlyData(months)

    const bdayAlerts = (custData || []).filter(c => c.birthday && getDateStatus(c.birthday, t) !== null)
      .sort((a, b) => (getDateStatus(a.birthday, t)?.days ?? 999) - (getDateStatus(b.birthday, t)?.days ?? 999))
    setBirthdayCustomers(bdayAlerts)

    const anniAlerts = (custData || []).filter(c => c.anniversary && getDateStatus(c.anniversary, t) !== null)
      .sort((a, b) => (getDateStatus(a.anniversary, t)?.days ?? 999) - (getDateStatus(b.anniversary, t)?.days ?? 999))
    setAnniversaryCustomers(anniAlerts)

    const winbackAlerts = (custData || []).filter(c => getWinbackStatus(c, statsMap) !== null)
      .sort((a, b) => getWinbackStatus(b, statsMap).daysSince - getWinbackStatus(a, statsMap).daysSince)
    setWinbackCustomers(winbackAlerts)

    setCustomers(custData || [])
    setLoading(false)
  }

  const fetchCustomerDetail = async (customer) => {
    setSelected(customer); setEditMode(false); setActiveTab('info'); setNewNote(''); setContactBy('Owner'); setEditForm(blankEdit(customer))
    const [ordRes, noteRes] = await Promise.all([
      supabase.from('orders').select('id, order_code, product_name, total_amount, status, created_at, order_source').eq('customer_id', customer.id).order('created_at', { ascending: false }),
      supabase.from('customer_notes').select('*').eq('customer_id', customer.id).order('created_at', { ascending: false }),
    ])
    setCustomerOrders(ordRes.data || [])
    setCustomerNotes(noteRes.data || [])
  }

  const handleToggleFavourite = async (e, customer) => {
    e.stopPropagation()
    const newVal = !customer.is_favourite
    await supabase.from('customers').update({ is_favourite: newVal }).eq('id', customer.id)
    setCustomers(cs => cs.map(c => c.id === customer.id ? { ...c, is_favourite: newVal } : c))
    if (selected?.id === customer.id) setSelected(s => ({ ...s, is_favourite: newVal }))
    setSummary(s => ({ ...s, favourites: s.favourites + (newVal ? 1 : -1) }))
  }

  const handleSaveEdit = async () => {
    setSavingEdit(true)
    await supabase.from('customers').update({
      name: editForm.name, phone: editForm.phone, email: editForm.email || null, whatsapp: editForm.whatsapp || null,
      facebook_url: editForm.facebook_url || null, instagram_url: editForm.instagram_url || null,
      neighbourhood: editForm.neighbourhood || null, metro_thana: editForm.metro_thana || null,
      thana: editForm.thana || null, district: editForm.district || null, division: editForm.division || null,
      moholla: editForm.moholla || null, house: editForm.house || null, road: editForm.road || null,
      zip: editForm.zip || null, country: editForm.country || null, tags: editForm.tags || null,
      birthday: editForm.birthday || null, anniversary: editForm.anniversary || null, gender: editForm.gender || null,
      age_range: editForm.age_range || null, interests: editForm.interests || null,
      hobbies: editForm.hobbies || null, occupation: editForm.occupation || null,
    }).eq('id', selected.id)
    const updated = { ...selected, ...editForm }
    setSelected(updated); setCustomers(cs => cs.map(c => c.id === selected.id ? updated : c))
    setEditMode(false); setSavingEdit(false); showToast(t('crm.toast_updated'))
  }

  const handleLogContact = async () => {
    if (!newNote.trim()) return
    setSavingNote(true)
    const now = new Date().toISOString()
    await supabase.from('customer_notes').insert({ customer_id: selected.id, seller_id: seller.id, note: newNote.trim(), created_by: contactBy })
    await supabase.from('customers').update({ last_contacted_at: now, last_contacted_by: contactBy }).eq('id', selected.id)
    setSelected(s => ({ ...s, last_contacted_at: now, last_contacted_by: contactBy }))
    setNewNote('')
    const { data } = await supabase.from('customer_notes').select('*').eq('customer_id', selected.id).order('created_at', { ascending: false })
    setCustomerNotes(data || [])
    setSavingNote(false); showToast(t('crm.toast_contact_logged'))
  }

  const handleDeleteNote = async (noteId) => {
    await supabase.from('customer_notes').delete().eq('id', noteId)
    setCustomerNotes(prev => prev.filter(n => n.id !== noteId))
  }

  const handleSendBirthdayWish = async (customer) => {
    const year = new Date().getFullYear()
    const template = bdaySettings || `Happy birthday {name}! Wishing you a wonderful day filled with joy. Thank you for being a valued customer — {shop_name}`
    const msg = template.replace('{name}', customer.name || 'there').replace('{shop_name}', seller?.shop_name || 'our shop')
    await navigator.clipboard.writeText(msg)
    await supabase.from('customers').update({ birthday_wished_year: year, birthday_wished_by: 'Owner', birthday_wished_at: new Date().toISOString() }).eq('id', customer.id)
    setCustomers(cs => cs.map(c => c.id === customer.id ? { ...c, birthday_wished_year: year, birthday_wished_by: 'Owner', birthday_wished_at: new Date().toISOString() } : c))
    setBirthdayCustomers(prev => prev.filter(c => c.id !== customer.id))
    if (selected?.id === customer.id) setSelected(s => ({ ...s, birthday_wished_year: year, birthday_wished_by: 'Owner', birthday_wished_at: new Date().toISOString() }))
    showToast(t('crm.toast_bday_copied'))
  }

  const handleSendAnniversaryWish = async (customer) => {
    const year = new Date().getFullYear()
    const template = anniSettings || `Happy anniversary {name}! It's been a year since you joined us — thank you for being part of our journey. Here's to many more! — {shop_name}`
    const msg = template.replace('{name}', customer.name || 'there').replace('{shop_name}', seller?.shop_name || 'our shop')
    await navigator.clipboard.writeText(msg)
    await supabase.from('customers').update({ anniversary_wished_year: year, anniversary_wished_by: 'Owner', anniversary_wished_at: new Date().toISOString() }).eq('id', customer.id)
    setCustomers(cs => cs.map(c => c.id === customer.id ? { ...c, anniversary_wished_year: year, anniversary_wished_by: 'Owner', anniversary_wished_at: new Date().toISOString() } : c))
    setAnniversaryCustomers(prev => prev.filter(c => c.id !== customer.id))
    if (selected?.id === customer.id) setSelected(s => ({ ...s, anniversary_wished_year: year, anniversary_wished_by: 'Owner', anniversary_wished_at: new Date().toISOString() }))
    showToast(t('crm.toast_anniversary_copied'))
  }

  const handleSendWinbackMessage = async (customer) => {
    const template = winbackSettings || `Hi {name}, we've missed you! It's been a while since your last order. Check out what's new — we'd love to have you back. — {shop_name}`
    const msg = template.replace('{name}', customer.name || 'there').replace('{shop_name}', seller?.shop_name || 'our shop')
    await navigator.clipboard.writeText(msg)
    const now = new Date().toISOString()
    await supabase.from('customers').update({ winback_contacted_at: now }).eq('id', customer.id)
    setCustomers(cs => cs.map(c => c.id === customer.id ? { ...c, winback_contacted_at: now } : c))
    setWinbackCustomers(prev => prev.filter(c => c.id !== customer.id))
    if (selected?.id === customer.id) setSelected(s => ({ ...s, winback_contacted_at: now }))
    showToast(t('crm.toast_winback_copied'))
  }

  const handleDelete = async () => {
    setDeleting(true)
    await supabase.from('customer_notes').delete().eq('customer_id', deleteId)
    await supabase.from('customers').delete().eq('id', deleteId)
    setDeleteId(null); setDeleting(false)
    if (selected?.id === deleteId) setSelected(null)
    await fetchCustomers(); showToast(t('crm.toast_deleted'))
  }

  const handleFlagCustomer = async ({ reasonCategory, reasonDetail }) => {
    if (!flagTarget) return
    await supabase.from('customer_flags').insert({
      phone: flagTarget.phone,
      flagged_by_seller_id: seller.id,
      reason_category: reasonCategory,
      reason_detail: reasonDetail || null,
      is_private: true,
      is_active: true,
    })
    const newFlag = { phone: flagTarget.phone, reason_category: reasonCategory, reason_detail: reasonDetail, created_at: new Date().toISOString(), is_active: true }
    setCustomerFlags(prev => ({ ...prev, [flagTarget.phone]: newFlag }))
    setShowFlagModal(false)
    showToast('Customer flagged')
  }

  const handleUnflagCustomer = async () => {
    if (!flagTarget) return
    const existingFlag = customerFlags[flagTarget.phone]
    if (!existingFlag) return
    await supabase.from('customer_flags').update({ is_active: false, resolved_at: new Date().toISOString() }).eq('id', existingFlag.id)
    setCustomerFlags(prev => { const next = { ...prev }; delete next[flagTarget.phone]; return next })
    setShowFlagModal(false)
    showToast('Flag removed')
  }

  const allSpends = Object.values(customerStats).map(s => s.total_spend)
  const avgSpend  = allSpends.length > 0 ? allSpends.reduce((a,b)=>a+b,0)/allSpends.length : 0
  const flaggedCount = Object.keys(customerFlags).length

  const filtered = customers.filter(c => {
    const q = search.toLowerCase()
    const matchSearch = !q || c.name?.toLowerCase().includes(q) || c.phone?.includes(q) || c.email?.toLowerCase().includes(q) || c.district?.toLowerCase().includes(q) || (c.metro_thana||c.thana||c.moholla)?.toLowerCase().includes(q)
    const s = customerStats[c.id] || {}
    const matchFilter =
      filter === 'all'        ? true :
      filter === 'favourites' ? c.is_favourite :
      filter === 'loyal'      ? (s.total_orders >= 5 || s.total_spend > avgSpend * 2) :
      filter === 'high_value' ? (s.total_spend > avgSpend * 1.5) :
      filter === 'returning'  ? (s.total_orders >= 2 && s.total_orders < 5) :
      filter === 'no_orders'  ? (s.total_orders === 0) :
      filter === 'new'        ? (new Date(c.created_at) >= new Date(new Date().getFullYear(), new Date().getMonth(), 1)) :
      filter === 'birthday'   ? getDateStatus(c.birthday, t) !== null :
      filter === 'anniversary'? getDateStatus(c.anniversary, t) !== null :
      filter === 'winback'    ? getWinbackStatus(c, customerStats) !== null :
      filter === 'flagged'    ? !!customerFlags[c.phone] :
      true
    const matchCol =
      (colFilters.country.length === 0  || colFilters.country.includes(c.country || '—')) &&
      (colFilters.division.length === 0 || colFilters.division.includes(c.division || '—')) &&
      (colFilters.district.length === 0 || colFilters.district.includes(c.district || '—')) &&
      (colFilters.gender.length === 0   || colFilters.gender.includes(c.gender || '—')) &&
      (colFilters.age_range.length === 0|| colFilters.age_range.includes(c.age_range || '—'))
    return matchSearch && matchFilter && matchCol
  })

  // Unique values for filter dropdowns
  const uniqueValues = (key) => [...new Set(customers.map(c => c[key] || '—').filter(Boolean))].sort()
  const countryOptions  = uniqueValues('country')
  const divisionOptions = uniqueValues('division')
  const districtOptions = uniqueValues('district')
  const genderOptions   = uniqueValues('gender')
  const ageOptions      = uniqueValues('age_range')
  const activeColFilterCount = Object.values(colFilters).reduce((n, arr) => n + arr.length, 0)

  // Sorting
  const getSortVal = (c, key) => {
    const s = customerStats[c.id] || {}
    switch (key) {
      case 'segment':    return getTier(c.id, customerStats, t).key
      case 'district':   return (c.district || '').toLowerCase()
      case 'country':    return (c.country || '').toLowerCase()
      case 'division':   return (c.division || '').toLowerCase()
      case 'orders':     return s.total_orders || 0
      case 'spend':      return s.total_spend || 0
      case 'last_order': return s.last_order ? new Date(s.last_order).getTime() : -1
      case 'added':      return new Date(c.created_at).getTime()
      default:           return ''
    }
  }
  const sorted = sortCol ? [...filtered].sort((a, b) => {
    const av = getSortVal(a, sortCol), bv = getSortVal(b, sortCol)
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1 : -1
    return 0
  }) : filtered

  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const totalPages = Math.ceil(sorted.length / CUSTOMERS_PER_PAGE)
  const pagedRows  = sorted.slice((currentPage - 1) * CUSTOMERS_PER_PAGE, currentPage * CUSTOMERS_PER_PAGE)

  useEffect(() => { setCurrentPage(1) }, [search, filter])

  // Animated KPI values
  const kpiTotal     = useCountUp(summary.total)
  const kpiActive    = useCountUp(summary.active)
  const kpiLoyal     = useCountUp(summary.loyal)
  const kpiHighValue = useCountUp(summary.highValue)
  const kpiNewMonth  = useCountUp(summary.newThisMonth)
  const kpiFlagged   = useCountUp(flaggedCount)

  if (!isFeatureAllowed('crm')) {
    return (
      <div style={{ flex: 1, background: 'var(--bg-page)', fontFamily: 'var(--font-body)' }}>
        <Topbar title={t('nav.crm')} />
        <div style={{ padding: '48px 24px', maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '40px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <FlatIcon name="users" size={40} color="var(--text-muted)" />
            </div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>{t('crm.upgrade_title')}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px', lineHeight: 1.6 }}>{t('crm.upgrade_desc')}</div>
            <button onClick={() => navigate('/pricing')}
              style={{ width: '100%', padding: '13px', borderRadius: '10px', background: 'var(--primary)', border: 'none', fontSize: '14px', fontWeight: 700, color: '#FFFFFF', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
              {t('crm.upgrade_btn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Insights chart data ──────────────────────────────────────────────
  const SEGMENT_COLORS = { loyal: 'var(--success)', high_value: 'var(--primary)', returning: PURPLE, active: 'var(--warning)', no_orders: 'var(--text-muted)' }
  const segmentMixData = (() => {
    const counts = { loyal: 0, high_value: 0, returning: 0, active: 0, no_orders: 0 }
    customers.forEach(c => { counts[getTier(c.id, customerStats, t).key]++ })
    return Object.entries(counts).map(([key, value]) => ({ label: t('crm.tier_' + key), value, color: SEGMENT_COLORS[key] }))
  })()

  const GENDER_COLORS = { Male: '#0EA5E9', Female: PURPLE, Other: 'var(--warning)' }
  const genderMixData = (() => {
    const counts = {}
    customers.forEach(c => { const g = c.gender || t('crm.not_specified'); counts[g] = (counts[g] || 0) + 1 })
    return Object.entries(counts).map(([label, value]) => ({ label, value, color: GENDER_COLORS[label] || 'var(--text-muted)' }))
  })()

  const ageMixData = (() => {
    const counts = {}
    customers.forEach(c => { const a = c.age_range || t('crm.not_specified'); counts[a] = (counts[a] || 0) + 1 })
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([label, value]) => ({ label, value }))
  })()
  const ageMixMax = Math.max(...ageMixData.map(a => a.value), 1)

  const districtData = (() => {
    const counts = {}
    customers.forEach(c => { if (c.district) counts[c.district] = (counts[c.district] || 0) + 1 })
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([label, value]) => ({ label, value }))
  })()

  const KPI_CARDS = [
    { label: t('crm.kpi_total'),      value: kpiTotal,     color: 'var(--text-primary)', icon: 'users' },
    { label: t('crm.kpi_active'),     value: kpiActive,    color: 'var(--success)',       icon: 'check' },
    { label: t('crm.kpi_loyal'),      value: kpiLoyal,     color: 'var(--primary)',       icon: 'badge' },
    { label: t('crm.kpi_high_value'), value: kpiHighValue, color: 'var(--warning)',       icon: 'diamond' },
    { label: t('crm.kpi_new_month'),  value: kpiNewMonth,  color: PURPLE,                  icon: 'plus' },
    { label: t('crm.kpi_flagged'),    value: kpiFlagged,   color: 'var(--error)',          icon: 'flag' },
  ]

  return (
    <div style={{ flex: 1, overflowX: 'hidden', background: 'var(--bg-page)', fontFamily: 'var(--font-body)' }}>
      <Topbar title={t('nav.crm')} />
      <div style={{ padding: '18px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{t('crm.title')}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{t('crm.subtitle')}</div>
        </div>

        {/* Birthday banner */}
        {birthdayCustomers.length > 0 && (
          <div className="pc-fade-up" style={{ background: 'var(--warning-light)', border: '1px solid var(--warning-border)', borderRadius: '12px', padding: '12px 16px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: 'var(--warning)', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>
              <FlatIcon name="gift" size={14} color="var(--warning)" />
              {birthdayCustomers.length} {birthdayCustomers.length !== 1 ? t('crm.bday_banner_pl') : t('crm.bday_banner')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {birthdayCustomers.map(c => {
                const bStatus = getDateStatus(c.birthday, t)
                const wished  = c.birthday_wished_year === new Date().getFullYear()
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-card)', borderRadius: '9px', padding: '10px 12px', flexWrap: 'wrap' }}>
                    <Avatar name={c.name} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{c.name}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{formatDateLong(c.birthday)} · {bStatus?.label}</div>
                      {wished && c.birthday_wished_by && (
                        <div style={{ fontSize: '11px', color: 'var(--success)' }}>{t('crm.bday_wished_by')} {c.birthday_wished_by} · {new Date(c.birthday_wished_at).toLocaleDateString()}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {!wished ? (
                        <button onClick={() => handleSendBirthdayWish(c)}
                          style={{ padding: '7px 14px', borderRadius: '8px', background: 'var(--warning)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                          {t('crm.bday_copy')}
                        </button>
                      ) : (
                        <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--success)', padding: '7px 10px' }}>{t('crm.bday_wished')}</span>
                      )}
                      <button onClick={() => fetchCustomerDetail(c)}
                        style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                        {t('crm.bday_view')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
            {!bdaySettings && <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--warning)' }}>{t('crm.bday_tip')}</div>}
          </div>
        )}

        {/* Anniversary banner */}
        {anniversaryCustomers.length > 0 && (
          <div className="pc-fade-up" style={{ background: PURPLE_BG, border: `1px solid ${PURPLE}33`, borderRadius: '12px', padding: '12px 16px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: PURPLE, marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>
              <FlatIcon name="gift" size={14} color={PURPLE} />
              {anniversaryCustomers.length} {anniversaryCustomers.length !== 1 ? t('crm.anniversary_banner_pl') : t('crm.anniversary_banner')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {anniversaryCustomers.map(c => {
                const aStatus = getDateStatus(c.anniversary, t)
                const wished  = c.anniversary_wished_year === new Date().getFullYear()
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-card)', borderRadius: '9px', padding: '10px 12px', flexWrap: 'wrap' }}>
                    <Avatar name={c.name} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{c.name}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{formatDateLong(c.anniversary)} · {aStatus?.label}</div>
                      {wished && c.anniversary_wished_by && (
                        <div style={{ fontSize: '11px', color: 'var(--success)' }}>{t('crm.anniversary_wished')} · {new Date(c.anniversary_wished_at).toLocaleDateString()}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {!wished ? (
                        <button onClick={() => handleSendAnniversaryWish(c)}
                          style={{ padding: '7px 14px', borderRadius: '8px', background: PURPLE, border: 'none', fontSize: '12px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                          {t('crm.anniversary_copy')}
                        </button>
                      ) : (
                        <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--success)', padding: '7px 10px' }}>{t('crm.anniversary_wished')}</span>
                      )}
                      <button onClick={() => fetchCustomerDetail(c)}
                        style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                        {t('crm.anniversary_view')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
            {!anniSettings && <div style={{ marginTop: '8px', fontSize: '11.5px', color: PURPLE }}>{t('crm.anniversary_tip')}</div>}
          </div>
        )}

        {/* Win-back banner */}
        {winbackCustomers.length > 0 && (
          <div className="pc-fade-up" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px 16px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-ui)' }}>
              <FlatIcon name="refresh" size={14} color="var(--text-primary)" />
              {winbackCustomers.length} {winbackCustomers.length !== 1 ? t('crm.winback_banner_pl') : t('crm.winback_banner')}
            </div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '10px' }}>{t('crm.winback_desc')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {winbackCustomers.slice(0, 5).map(c => {
                const wStatus = getWinbackStatus(c, customerStats)
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-muted)', borderRadius: '9px', padding: '10px 12px', flexWrap: 'wrap' }}>
                    <Avatar name={c.name} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{c.name}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{wStatus?.daysSince}d {t('crm.days')} · {fmt(customerStats[c.id]?.total_spend || 0)} {t('crm.stat_total_spend').toLowerCase()}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={() => handleSendWinbackMessage(c)}
                        style={{ padding: '7px 14px', borderRadius: '8px', background: 'var(--primary)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                        {t('crm.winback_copy')}
                      </button>
                      <button onClick={() => fetchCustomerDetail(c)}
                        style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                        {t('crm.winback_view')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* KPI cards */}
        <div className="crm-kpi-row">
          {KPI_CARDS.map((s, i) => (
            <div key={i} className="crm-kpi-card pc-card-hover pc-fade-up" style={{ animationDelay: (i * 0.05) + 's', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '12px 14px' }}>
              <div style={{ marginBottom: '6px' }}><FlatIcon name={s.icon} size={16} color={s.color} /></div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color, marginBottom: '2px', fontFamily: 'var(--font-ui)' }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="crm-chart-grid">
          <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-ui)' }}>{t('crm.chart_new_monthly')}</div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('crm.chart_new_monthly_sub')}</div>
            <MiniBar data={monthlyData} color="var(--primary)" height={60} />
          </div>
          <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', fontFamily: 'var(--font-ui)' }}>{t('crm.chart_top_districts')}</div>
            {districtData.length === 0 ? (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px' }}>{t('crm.no_chart_data')}</div>
            ) : districtData.map((loc, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                <div style={{ fontSize: '11.5px', color: 'var(--text-primary)', minWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}>{loc.label}</div>
                <AnimatedBar pct={Math.round((loc.value / districtData[0].value) * 100)} color="var(--primary)" delay={i * 60} />
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--primary)', minWidth: '20px', textAlign: 'right', fontFamily: 'var(--font-ui)' }}>{loc.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Insights */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>{t('crm.insights_title')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }} className="crm-insights-grid">
            <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', fontFamily: 'var(--font-ui)' }}>{t('crm.segment_mix_title')}</div>
              <DonutChart data={segmentMixData} t={t} />
            </div>
            <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', fontFamily: 'var(--font-ui)' }}>{t('crm.gender_mix_title')}</div>
              <DonutChart data={genderMixData} t={t} />
            </div>
            <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', fontFamily: 'var(--font-ui)' }}>{t('crm.age_mix_title')}</div>
              {ageMixData.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px' }}>{t('crm.no_chart_data')}</div>
              ) : ageMixData.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                  <div style={{ fontSize: '11.5px', color: 'var(--text-primary)', minWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}>{a.label}</div>
                  <AnimatedBar pct={Math.round((a.value / ageMixMax) * 100)} color={PURPLE} delay={i * 60} />
                  <div style={{ fontSize: '11px', fontWeight: 700, color: PURPLE, minWidth: '20px', textAlign: 'right', fontFamily: 'var(--font-ui)' }}>{a.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue summary */}
        <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '2px' }}>{t('crm.rev_total')}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--primary)', fontFamily: 'var(--font-ui)' }}>{fmt(summary.totalRevenue)}</div>
          </div>
          <div style={{ width: '1px', height: '40px', background: 'var(--border)' }} />
          <div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '2px' }}>{t('crm.rev_avg')}</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{fmt(summary.active > 0 ? summary.totalRevenue / summary.active : 0)}</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <SegmentGuide t={t} />
          </div>
        </div>

        {/* Search + Filters + Columns */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('crm.search_placeholder')}
              style={{ ...inp, paddingLeft: '36px' }}
              onFocus={e => e.target.style.borderColor = 'var(--primary)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <FlatIcon name="search" size={13} color="var(--text-muted)" />
            </div>
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>
            </button>}
          </div>
          <button onClick={() => setShowFiltersPanel(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '9px', border: '1px solid', borderColor: activeColFilterCount > 0 ? 'var(--primary)' : 'var(--border)', background: activeColFilterCount > 0 ? 'var(--primary-light)' : 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: activeColFilterCount > 0 ? 'var(--primary)' : 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)', flexShrink: 0 }}>
            <FlatIcon name="filter" size={13} color={activeColFilterCount > 0 ? 'var(--primary)' : 'var(--text-muted)'} />
            {t('crm.filters_btn')}{activeColFilterCount > 0 ? ` (${activeColFilterCount})` : ''}
          </button>
          <ColumnsMenu visible={visibleCols} onChange={setVisibleCols} t={t} />
        </div>

        {showFiltersPanel && (
          <div className="pc-fade-up" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px', padding: '10px 12px', borderRadius: '10px', background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
            <MultiSelectFilter label={t('crm.filter_country')} options={countryOptions} selected={colFilters.country} onChange={v => setColFilters(f => ({ ...f, country: v }))} />
            <MultiSelectFilter label={t('crm.filter_division')} options={divisionOptions} selected={colFilters.division} onChange={v => setColFilters(f => ({ ...f, division: v }))} />
            <MultiSelectFilter label={t('crm.col_district')} options={districtOptions} selected={colFilters.district} onChange={v => setColFilters(f => ({ ...f, district: v }))} />
            <MultiSelectFilter label={t('crm.filter_gender')} options={genderOptions} selected={colFilters.gender} onChange={v => setColFilters(f => ({ ...f, gender: v }))} />
            <MultiSelectFilter label={t('crm.filter_age_group')} options={ageOptions} selected={colFilters.age_range} onChange={v => setColFilters(f => ({ ...f, age_range: v }))} />
            {activeColFilterCount > 0 && (
              <button onClick={() => setColFilters({ country: [], division: [], district: [], gender: [], age_range: [] })}
                style={{ fontSize: '12px', fontWeight: 600, color: 'var(--error)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                {t('crm.clear_filters')}
              </button>
            )}
          </div>
        )}

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {[
            { key: 'all',         label: t('crm.filter_all'),         count: customers.length,            icon: null },
            { key: 'favourites',  label: t('crm.filter_favourites'),  count: summary.favourites,          icon: 'star' },
            { key: 'loyal',       label: t('crm.filter_loyal'),       count: summary.loyal,               icon: 'badge' },
            { key: 'high_value',  label: t('crm.filter_high_value'),  count: summary.highValue,           icon: 'diamond' },
            { key: 'returning',   label: t('crm.filter_returning'),   count: null,                         icon: 'refresh' },
            { key: 'new',         label: t('crm.filter_new'),         count: summary.newThisMonth,        icon: 'plus' },
            { key: 'birthday',    label: t('crm.filter_birthday'),    count: birthdayCustomers.length,    icon: 'gift' },
            { key: 'anniversary', label: t('crm.filter_anniversary'), count: anniversaryCustomers.length, icon: 'gift' },
            { key: 'winback',     label: t('crm.filter_winback'),     count: winbackCustomers.length,     icon: 'refresh' },
            { key: 'flagged',     label: t('crm.filter_flagged'),     count: flaggedCount,                icon: 'flag' },
            { key: 'no_orders',   label: t('crm.filter_no_orders'),   count: null,                         icon: null },
          ].map(f => {
            const active = filter === f.key
            const activeColor = f.key === 'flagged' ? 'var(--error)' : 'var(--primary)'
            return (
              <button key={f.key} onClick={() => setFilter(f.key)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderRadius: '20px', border: '1px solid', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: active ? activeColor : 'var(--bg-card)', color: active ? '#FFFFFF' : 'var(--text-muted)', borderColor: active ? activeColor : 'var(--border)', fontFamily: 'var(--font-ui)' }}>
                {f.icon && <FlatIcon name={f.icon} size={11} color={active ? '#FFFFFF' : 'var(--text-muted)'} />}
                {f.label}{f.count !== null ? <span style={{ opacity: 0.75, marginLeft: '2px' }}>{f.count}</span> : ''}
              </button>
            )
          })}
        </div>

        {search && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>{sorted.length} {t('crm.results_for')} "{search}"</div>}

        {/* Table */}
        {loading ? (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', animation: 'crm-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('crm.loading')}</div>
          </div>
        ) : sorted.length === 0 ? (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}><FlatIcon name="users" size={36} color="var(--text-muted)" /></div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px', fontFamily: 'var(--font-ui)' }}>{customers.length === 0 ? t('crm.no_customers_yet') : t('crm.no_customers_match')}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{t('crm.no_customers_desc')}</div>
          </div>
        ) : (
          <>
            {/* Desktop table — horizontal scroll, column visibility, sort */}
            <div className="crm-desktop" style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '12px' }}>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: visibleCols.reduce((sum, k) => sum + (ALL_COLUMNS.find(c => c.key === k)?.width || 100), 0) + (visibleCols.length - 1) * 8 + 'px' }}>
                  {/* Header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: visibleCols.map(k => (ALL_COLUMNS.find(c => c.key === k)?.width || 100) + 'px').join(' '), gap: '8px', padding: '10px 16px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    {visibleCols.map(key => {
                      const col = ALL_COLUMNS.find(c => c.key === key)
                      return (
                        <div key={key} onClick={() => col.sortable && toggleSort(key)}
                          style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-ui)', cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none' }}>
                          {t('crm.' + col.label)}
                          {col.sortable && (
                            <svg width="9" height="9" viewBox="0 0 20 20" fill={sortCol === key ? 'var(--primary)' : 'var(--border)'} style={{ transform: sortCol === key && sortDir === 'desc' ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                              <path d="M10 4l5 6H5z" />
                            </svg>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {pagedRows.map((customer, idx) => {
                    const s     = customerStats[customer.id] || { total_orders: 0, total_spend: 0, last_order: null }
                    const tier  = getTier(customer.id, customerStats, t)
                    const locRem = formatLocationRemaining(customer)
                    const flag  = getFlag(customer.country_code)
                    const bday  = getDateStatus(customer.birthday, t)
                    const anni  = getDateStatus(customer.anniversary, t)
                    const winback = getWinbackStatus(customer, customerStats)
                    const isSel = selected?.id === customer.id
                    const isFlagged = !!customerFlags[customer.phone]
                    const hasCommunityFlag = !!communityFlags[customer.phone]
                    const cfgLevel = hasCommunityFlag ? FLAG_LEVEL_CONFIG[communityFlags[customer.phone]?.level] : null

                    const cell = (key) => {
                      switch (key) {
                        case 'sl': return <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)' }}>{(currentPage - 1) * CUSTOMERS_PER_PAGE + idx + 1}</span>
                        case 'customer': return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <Avatar name={customer.name} size={30} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.name || '—'}</span>
                                {flag && <span style={{ fontSize: '14px', flexShrink: 0 }}>{flag}</span>}
                              </div>
                            </div>
                          </div>
                        )
                        case 'phone': return <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{customer.phone || '—'}</span>
                        case 'segment': return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '20px', background: tier.bg, color: tier.color, whiteSpace: 'nowrap' }}>
                            <FlatIcon name={tier.icon} size={9} color={tier.color} />{tier.label}
                          </span>
                        )
                        case 'district': return <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{customer.district || '—'}</span>
                        case 'location': return <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{locRem}</span>
                        case 'orders': return <span style={{ fontSize: '13px', fontWeight: 700, color: s.total_orders > 0 ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{s.total_orders}</span>
                        case 'spend': return <span style={{ fontSize: '12.5px', fontWeight: 700, color: s.total_spend > 0 ? 'var(--primary)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{s.total_spend > 0 ? fmt(s.total_spend) : '—'}</span>
                        case 'last_order': return <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{s.last_order ? new Date(s.last_order).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</span>
                        case 'added': return <span style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{new Date(customer.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                        case 'country': return <span style={{ fontSize: '11.5px', color: 'var(--text-primary)' }}>{customer.country || '—'}</span>
                        case 'division': return <span style={{ fontSize: '11.5px', color: 'var(--text-primary)' }}>{customer.division || '—'}</span>
                        case 'gender': return <span style={{ fontSize: '11.5px', color: 'var(--text-primary)' }}>{customer.gender || '—'}</span>
                        case 'age_range': return <span style={{ fontSize: '11.5px', color: 'var(--text-primary)' }}>{customer.age_range || '—'}</span>
                        case 'flags': return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {bday && <span title={t('crm.filter_birthday')}><FlatIcon name="gift" size={13} color="var(--warning)" /></span>}
                            {anni && <span title={t('crm.filter_anniversary')}><FlatIcon name="gift" size={13} color={PURPLE} /></span>}
                            {winback && <span title={t('crm.filter_winback')}><FlatIcon name="refresh" size={13} color="var(--text-muted)" /></span>}
                            {isFlagged && <span title={t('crm.filter_flagged')}><FlatIcon name="flag" size={13} color="var(--error)" /></span>}
                            {hasCommunityFlag && cfgLevel && <span title={'Community ' + cfgLevel.label}><FlatIcon name="flag" size={13} color={cfgLevel.color} /></span>}
                          </div>
                        )
                        case 'actions': return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }} onClick={e => e.stopPropagation()}>
                            <button onClick={e => handleToggleFavourite(e, customer)} title={t('crm.toggle_favourite')}
                              style={{ width: '26px', height: '26px', borderRadius: '7px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <FlatIcon name="star" size={14} color={customer.is_favourite ? 'var(--warning)' : 'var(--border)'} />
                            </button>
                            <button onClick={() => fetchCustomerDetail(customer)} title={t('crm.tab_info')}
                              style={{ width: '26px', height: '26px', borderRadius: '7px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <FlatIcon name="external" size={13} color="var(--primary)" />
                            </button>
                            <button onClick={() => { setFlagTarget(customer); setShowFlagModal(true) }}
                              title={isFlagged ? 'Manage flag' : 'Flag customer'}
                              style={{ width: '26px', height: '26px', borderRadius: '7px', border: 'none', background: isFlagged ? 'var(--error-light)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--error-light)'}
                              onMouseLeave={e => e.currentTarget.style.background = isFlagged ? 'var(--error-light)' : 'transparent'}>
                              <FlatIcon name="flag" size={13} color={isFlagged ? 'var(--error)' : 'var(--text-muted)'} />
                            </button>
                            <button onClick={() => setDeleteId(customer.id)}
                              style={{ width: '26px', height: '26px', borderRadius: '7px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--error-light)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <FlatIcon name="trash" size={13} color="var(--error)" />
                            </button>
                          </div>
                        )
                        default: return null
                      }
                    }

                    return (
                      <div key={customer.id}
                        style={{ display: 'grid', gridTemplateColumns: visibleCols.map(k => (ALL_COLUMNS.find(c => c.key === k)?.width || 100) + 'px').join(' '), gap: '8px', padding: '11px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer', transition: 'background 0.1s', background: isSel ? 'var(--primary-light)' : 'var(--bg-card)' }}
                        onClick={() => fetchCustomerDetail(customer)}
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--bg-muted)' }}
                        onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = isSel ? 'var(--primary-light)' : 'var(--bg-card)' }}>
                        {visibleCols.map(key => <div key={key} style={{ minWidth: 0 }}>{cell(key)}</div>)}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Mobile cards — consistent layout for every customer */}
            <div className="crm-mobile">
              {pagedRows.map((customer, idx) => {
                const s     = customerStats[customer.id] || { total_orders: 0, total_spend: 0, last_order: null }
                const tier  = getTier(customer.id, customerStats, t)
                const locRem = formatLocationRemaining(customer)
                const flag  = getFlag(customer.country_code)
                const bday  = getDateStatus(customer.birthday, t)
                const anni  = getDateStatus(customer.anniversary, t)
                const winback = getWinbackStatus(customer, customerStats)
                const isFlagged = !!customerFlags[customer.phone]

                return (
                  <div key={customer.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => fetchCustomerDetail(customer)}>

                    {/* Row 1: SL + avatar + name + phone ... tier badge + status icons */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                        <span style={{ fontSize: '10.5px', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>#{(currentPage - 1) * CUSTOMERS_PER_PAGE + idx + 1}</span>
                        <Avatar name={customer.name} size={34} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{customer.name || '—'}</span>
                            {flag && <span>{flag}</span>}
                          </div>
                          <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>{customer.phone || '—'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flexShrink: 0 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: 'var(--radius-full)', background: tier.bg, color: tier.color, whiteSpace: 'nowrap' }}>
                          <FlatIcon name={tier.icon} size={9} color={tier.color} />{tier.label}
                        </span>
                        {(bday || anni || winback || isFlagged) && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {bday && <FlatIcon name="gift" size={13} color="var(--warning)" />}
                            {anni && <FlatIcon name="gift" size={13} color={PURPLE} />}
                            {winback && <FlatIcon name="refresh" size={13} color="var(--text-muted)" />}
                            {isFlagged && <FlatIcon name="flag" size={13} color="var(--error)" />}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Location | Orders & Spend — two columns with headers */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px', fontFamily: 'var(--font-ui)' }}>{t('crm.col_location')}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}>{customer.district || '—'}</div>
                        {locRem !== '—' && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}>{locRem}</div>}
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px', fontFamily: 'var(--font-ui)' }}>{t('crm.stat_orders')} · {t('crm.col_spend')}</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{s.total_orders}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>{s.total_spend > 0 ? fmt(s.total_spend) : '—'}</div>
                      </div>
                    </div>

                    {/* Row 3: Action pills — matches Orders' full-width button row */}
                    <div style={{ display: 'flex', gap: '6px', paddingTop: '10px', borderTop: '1px solid var(--border)' }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => handleToggleFavourite({ stopPropagation: () => {} }, customer)}
                        style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FlatIcon name="star" size={13} color={customer.is_favourite ? 'var(--warning)' : 'var(--text-muted)'} />
                      </button>
                      {customer.phone && (
                        <a href={'tel:' + customer.phone} onClick={e => e.stopPropagation()}
                          style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                          <FlatIcon name="phone" size={13} color="var(--success)" />
                        </a>
                      )}
                      {customer.whatsapp && (
                        <a href={'https://wa.me/' + customer.whatsapp.replace(/[^0-9]/g, '')} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                          <FlatIcon name="chat" size={13} color="#16A34A" />
                        </a>
                      )}
                      <button onClick={() => { setFlagTarget(customer); setShowFlagModal(true) }}
                        style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FlatIcon name="flag" size={13} color={isFlagged ? 'var(--error)' : 'var(--text-muted)'} />
                      </button>
                      <button onClick={() => setDeleteId(customer.id)}
                        style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FlatIcon name="trash" size={13} color="var(--error)" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {t('crm.showing')} {(currentPage-1)*CUSTOMERS_PER_PAGE+1}–{Math.min(currentPage*CUSTOMERS_PER_PAGE, sorted.length)} {t('crm.of')} {sorted.length}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: currentPage===1 ? 'var(--text-muted)' : 'var(--text-primary)', cursor: currentPage===1 ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)' }}>
                    {t('crm.prev')}
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setCurrentPage(p)}
                      style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid', borderColor: p===currentPage ? 'var(--primary)' : 'var(--border)', background: p===currentPage ? 'var(--primary)' : 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: p===currentPage ? '#FFFFFF' : 'var(--text-primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage===totalPages}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: currentPage===totalPages ? 'var(--text-muted)' : 'var(--text-primary)', cursor: currentPage===totalPages ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)' }}>
                    {t('crm.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Customer detail — desktop centered modal / mobile full-screen sheet */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: isMobile ? 'var(--bg-card)' : 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '16px' }}
          onClick={e => !isMobile && e.target === e.currentTarget && setSelected(null)}>
          <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: isMobile ? 'none' : '560px', borderRadius: isMobile ? 0 : '16px', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-body)', overflow: 'hidden' }}>

            {/* Modal header */}
            <div style={{ padding: isMobile ? '14px 16px' : '16px 20px', background: 'var(--primary)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <Avatar name={selected.name} size={44} light />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#FFFFFF', fontFamily: 'var(--font-ui)' }}>{selected.name}</div>
                    {getFlag(selected.country_code) && <span style={{ fontSize: '18px' }}>{getFlag(selected.country_code)}</span>}
                    <button onClick={e => handleToggleFavourite(e, selected)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                      <FlatIcon name="star" size={16} color={selected.is_favourite ? '#FDE68A' : 'rgba(255,255,255,0.5)'} />
                    </button>
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '2px' }}>{selected.phone}</div>
                  {selected.email && <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.7)' }}>{selected.email}</div>}
                  {/* Flag badges in modal header */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                    {customerFlags[selected.phone] && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: 'rgba(220,38,38,0.25)', color: '#FCA5A5' }}>
                        <FlatIcon name="flag" size={9} color="#FCA5A5" />{t('crm.filter_flagged')}
                      </span>
                    )}
                    {communityFlags[selected.phone] && (
                      <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: 'rgba(234,88,12,0.25)', color: '#FED7AA' }}>
                        {'Community ' + FLAG_LEVEL_CONFIG[communityFlags[selected.phone]?.level]?.label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '10px' }}>
                <button onClick={() => setEditMode(!editMode)}
                  style={{ padding: '6px 12px', borderRadius: '8px', background: editMode ? 'var(--error-light)' : 'rgba(255,255,255,0.2)', border: 'none', fontSize: '12px', fontWeight: 600, color: editMode ? 'var(--error)' : '#FFFFFF', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                  {editMode ? t('crm.cancel_btn') : t('crm.edit_btn')}
                </button>
                <button onClick={() => setSelected(null)}
                  style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>
                </button>
              </div>
            </div>

            {/* Quick contact row */}
            {!editMode && (
              <div style={{ padding: '14px 20px 0' }}>
                <QuickContact customer={selected} t={t} onNewOrder={() => { setSelected(null); navigate('/orders/add') }} />
              </div>
            )}

            {/* Stats row */}
            {!editMode && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                {(() => {
                  const s    = customerStats[selected.id] || { total_orders: 0, total_spend: 0 }
                  const tier = getTier(selected.id, customerStats, t)
                  return [
                    { label: t('crm.stat_orders'),      value: s.total_orders,                               color: 'var(--text-primary)' },
                    { label: t('crm.stat_total_spend'), value: s.total_spend > 0 ? fmt(s.total_spend) : '—', color: 'var(--primary)' },
                    { label: t('crm.stat_segment'),     value: tier.label,                                   color: tier.color, small: true, icon: tier.icon },
                  ].map((stat, i) => (
                    <div key={i} style={{ padding: '12px', textAlign: 'center', borderRight: i < 2 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: stat.small ? '13px' : '20px', fontWeight: 700, color: stat.color, fontFamily: 'var(--font-ui)' }}>
                        {stat.icon && <FlatIcon name={stat.icon} size={13} color={stat.color} />}
                        {stat.value}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{stat.label}</div>
                    </div>
                  ))
                })()}
              </div>
            )}

            {/* Community flag warning in modal */}
            {!editMode && communityFlags[selected.phone] && (() => {
              const cf = communityFlags[selected.phone]
              const cfg = FLAG_LEVEL_CONFIG[cf.level] || FLAG_LEVEL_CONFIG.watch
              return (
                <div style={{ padding: '10px 16px', background: cfg.bg, borderBottom: `1px solid ${cfg.border}`, flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 700, color: cfg.color, marginBottom: '2px', fontFamily: 'var(--font-ui)' }}>
                    <FlatIcon name="flag" size={11} color={cfg.color} /> Community Flag — {cfg.label}
                  </div>
                  <div style={{ fontSize: '11.5px', color: cfg.color }}>
                    Reported by {cf.flag_count} seller{cf.flag_count !== 1 ? 's' : ''} for: {cf.dominant_reason?.replace(/_/g, ' ')}
                  </div>
                </div>
              )
            })()}

            {/* Tabs */}
            {!editMode && (
              <div style={{ display: 'flex', gap: '2px', padding: '8px 16px 0', borderBottom: '1px solid var(--border)', flexShrink: 0, overflowX: 'auto' }}>
                {[
                  { key: 'info',     label: t('crm.tab_info') },
                  { key: 'about',    label: t('crm.tab_about') },
                  { key: 'orders',   label: t('crm.tab_orders') + ' (' + customerOrders.length + ')' },
                  { key: 'contact',  label: t('crm.tab_contact') + ' (' + customerNotes.length + ')' },
                  { key: 'timeline', label: t('crm.tab_timeline') },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    style={{ padding: '7px 14px', borderRadius: '8px 8px 0 0', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.key ? 'var(--bg-card)' : 'transparent', color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-muted)', borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)' }}>
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Modal body */}
            <div style={{ overflowY: 'auto', flex: 1 }}>

              {/* Edit form */}
              {editMode && (
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>{t('crm.edit_contact_section')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }} className="crm-form-grid">
                    <div style={{ gridColumn: 'span 2' }}><Inp label={t('crm.name_label')} value={editForm.name} onChange={v => setEF('name', v)} placeholder="Full name" /></div>
                    <Inp label={t('crm.phone_label')} value={editForm.phone} onChange={v => setEF('phone', v)} placeholder="+880..." />
                    <Inp label={t('crm.whatsapp_label')} value={editForm.whatsapp} onChange={v => setEF('whatsapp', v)} placeholder="WhatsApp number" optional />
                    <Inp label={t('crm.email_label')} type="email" value={editForm.email} onChange={v => setEF('email', v)} placeholder="email@example.com" optional />
                    <Inp label={t('crm.facebook_label')} value={editForm.facebook_url} onChange={v => setEF('facebook_url', v)} placeholder="facebook.com/..." optional />
                    <div style={{ gridColumn: 'span 2' }}><Inp label={t('crm.instagram_label')} value={editForm.instagram_url} onChange={v => setEF('instagram_url', v)} placeholder="instagram.com/..." optional /></div>
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>{t('crm.edit_address_section')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }} className="crm-form-grid">
                    <Inp label={t('crm.neighbourhood_label')} value={editForm.neighbourhood} onChange={v => setEF('neighbourhood', v)} placeholder="e.g. Block D" optional />
                    <Inp label={t('crm.thana_label')} value={editForm.metro_thana || editForm.thana} onChange={v => setEF('metro_thana', v)} placeholder="e.g. Mirpur 2" optional />
                    <Inp label={t('crm.moholla_label')} value={editForm.moholla} onChange={v => setEF('moholla', v)} placeholder="e.g. Monipur" optional />
                    <Inp label={t('crm.district_label')} value={editForm.district} onChange={v => setEF('district', v)} placeholder="e.g. Dhaka" optional />
                    <Inp label={t('crm.division_label')} value={editForm.division} onChange={v => setEF('division', v)} placeholder="e.g. Dhaka" optional />
                    <Inp label={t('crm.house_label')} value={editForm.house} onChange={v => setEF('house', v)} placeholder="e.g. 957" optional />
                    <Inp label={t('crm.road_label')} value={editForm.road} onChange={v => setEF('road', v)} placeholder="e.g. Road 5" optional />
                    <Inp label={t('crm.zip_label')} value={editForm.zip} onChange={v => setEF('zip', v)} placeholder="e.g. 1216" optional />
                    <div style={{ gridColumn: 'span 2' }}><Inp label={t('crm.country_label')} value={editForm.country} onChange={v => setEF('country', v)} placeholder="e.g. Bangladesh" optional /></div>
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>{t('crm.edit_personal_section')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }} className="crm-form-grid">
                    <Inp label={t('crm.birthday_label')} type="date" value={editForm.birthday} onChange={v => setEF('birthday', v)} optional />
                    <Inp label={t('crm.anniversary_label_field')} type="date" value={editForm.anniversary} onChange={v => setEF('anniversary', v)} optional />
                    <div>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui)' }}>{t('crm.gender_field')} <span style={{ fontWeight: 400, opacity: 0.7 }}>optional</span></label>
                      <select value={editForm.gender} onChange={e => setEF('gender', e.target.value)} style={{ ...inp }}
                        onFocus={e => e.target.style.borderColor = 'var(--primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                        <option value="">{t('crm.gender_not_specified')}</option>
                        <option>Female</option><option>Male</option><option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui)' }}>{t('crm.age_range_label')} <span style={{ fontWeight: 400, opacity: 0.7 }}>optional</span></label>
                      <select value={editForm.age_range} onChange={e => setEF('age_range', e.target.value)} style={{ ...inp }}
                        onFocus={e => e.target.style.borderColor = 'var(--primary)'} onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                        {AGE_GROUPS.map(a => <option key={a} value={a === 'Not specified' ? '' : a}>{a}</option>)}
                      </select>
                    </div>
                    <Inp label={t('crm.occupation_label_field')} value={editForm.occupation} onChange={v => setEF('occupation', v)} placeholder={t('crm.occupation_placeholder')} optional />
                    <div style={{ gridColumn: 'span 2' }}><Inp label={t('crm.interests_field')} value={editForm.interests} onChange={v => setEF('interests', v)} placeholder={t('crm.interests_placeholder')} optional /></div>
                    <div style={{ gridColumn: 'span 2' }}><Inp label={t('crm.hobbies_field')} value={editForm.hobbies} onChange={v => setEF('hobbies', v)} placeholder={t('crm.hobbies_placeholder')} optional /></div>
                    <div style={{ gridColumn: 'span 2' }}><Inp label={t('crm.tags_label')} value={editForm.tags} onChange={v => setEF('tags', v)} placeholder={t('crm.tags_placeholder')} optional /></div>
                  </div>
                  <button onClick={handleSaveEdit} disabled={savingEdit}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#FFFFFF', cursor: 'pointer', opacity: savingEdit ? 0.6 : 1, marginBottom: '16px', fontFamily: 'var(--font-ui)' }}>
                    {savingEdit ? t('crm.saving_btn') : t('crm.save_btn')}
                  </button>
                </div>
              )}

              {/* Info tab */}
              {!editMode && activeTab === 'info' && (
                <div style={{ padding: '16px 20px' }}>
                  <Sparkline orders={customerOrders} t={t} />

                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>{t('crm.contact_section')}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '14px' }}>
                    {[
                      { icon: 'phone', label: t('crm.phone_label'), value: selected.phone, href: 'tel:' + selected.phone },
                      { icon: 'chat', label: 'WhatsApp', value: selected.whatsapp, href: 'https://wa.me/' + (selected.whatsapp || '').replace(/[^0-9]/g, '') },
                      { icon: 'mail', label: t('crm.email_label'), value: selected.email, href: 'mailto:' + selected.email },
                      { icon: 'external', label: 'Facebook', value: selected.facebook_url, href: selected.facebook_url },
                      { icon: 'external', label: 'Instagram', value: selected.instagram_url, href: selected.instagram_url },
                    ].filter(f => f.value).map((f, i) => (
                      <a key={i} href={f.href} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: '9px', background: 'var(--bg-muted)', border: '1px solid var(--border)', textDecoration: 'none', color: 'var(--text-primary)', fontSize: '12.5px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontWeight: 600 }}><FlatIcon name={f.icon} size={13} />{f.label}</span>
                        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{f.value}</span>
                      </a>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>{t('crm.address_section')}</div>
                  <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'var(--bg-muted)', fontSize: '12.5px', color: 'var(--text-primary)', lineHeight: 1.8, marginBottom: '14px' }}>{formatFullAddress(selected)}</div>
                  {selected.tags && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                      {selected.tags.split(',').map(tag => tag.trim()).filter(Boolean).map((tag, i) => (
                        <span key={i} style={{ fontSize: '11.5px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', background: 'var(--primary-light)', color: 'var(--primary)' }}>#{tag}</span>
                      ))}
                    </div>
                  )}
                  {selected.last_contacted_at && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 12px', borderRadius: '9px', background: 'var(--success-light)', border: '1px solid var(--success-border)', marginBottom: '14px', fontSize: '12px', color: 'var(--success)' }}>
                      <FlatIcon name="check" size={13} color="var(--success)" />
                      {t('crm.last_contacted')} {new Date(selected.last_contacted_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })} {t('crm.by')} {selected.last_contacted_by || 'Owner'}
                    </div>
                  )}
                  {customerStats[selected.id]?.source && (
                    <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                      {t('crm.source')} <strong style={{ color: 'var(--text-primary)' }}>{customerStats[selected.id].source}</strong>
                    </div>
                  )}
                  <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                    {t('crm.added')} {new Date(selected.created_at).toLocaleString('en-BD', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>

                  <button onClick={() => { setFlagTarget(selected); setShowFlagModal(true) }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', borderRadius: '10px', border: `1px solid ${customerFlags[selected.phone] ? 'var(--success-border)' : 'var(--error-border)'}`, background: customerFlags[selected.phone] ? 'var(--success-light)' : 'var(--error-light)', fontSize: '12.5px', fontWeight: 600, color: customerFlags[selected.phone] ? 'var(--success)' : 'var(--error)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                    <FlatIcon name="flag" size={13} color={customerFlags[selected.phone] ? 'var(--success)' : 'var(--error)'} />
                    {customerFlags[selected.phone] ? 'Flagged — tap to manage or remove' : 'Flag this customer'}
                  </button>
                </div>
              )}

              {/* About tab */}
              {!editMode && activeTab === 'about' && (
                <div style={{ padding: '16px 20px' }}>
                  {(() => {
                    const fields = [
                      { label: t('crm.bday_label'), value: selected.birthday ? formatDateLong(selected.birthday) + (getDateStatus(selected.birthday, t) ? ' — ' + getDateStatus(selected.birthday, t)?.label : '') : null },
                      { label: t('crm.anniversary_label'), value: selected.anniversary ? formatDateLong(selected.anniversary) + (getDateStatus(selected.anniversary, t) ? ' — ' + getDateStatus(selected.anniversary, t)?.label : '') : null },
                      { label: t('crm.gender_label'), value: selected.gender },
                      { label: t('crm.age_label'), value: selected.age_range },
                      { label: t('crm.occupation_label'), value: selected.occupation },
                      { label: t('crm.interests_label'), value: selected.interests },
                      { label: t('crm.hobbies_label'), value: selected.hobbies },
                    ].filter(f => f.value)

                    if (fields.length === 0) return (
                      <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}><FlatIcon name="user" size={28} color="var(--text-muted)" /></div>
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', fontFamily: 'var(--font-ui)' }}>{t('crm.no_profile')}</div>
                        <div style={{ fontSize: '12px' }}>{t('crm.no_profile_desc')}</div>
                      </div>
                    )

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {fields.map((f, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '9px', background: 'var(--bg-muted)', fontSize: '12.5px' }}>
                            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{f.label}</span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{f.value}</span>
                          </div>
                        ))}
                        {selected.birthday && (
                          <div style={{ marginTop: '8px', padding: '12px 14px', borderRadius: '10px', background: selected.birthday_wished_year === new Date().getFullYear() ? 'var(--success-light)' : 'var(--warning-light)', border: '1px solid ' + (selected.birthday_wished_year === new Date().getFullYear() ? 'var(--success-border)' : 'var(--warning-border)') }}>
                            {selected.birthday_wished_year === new Date().getFullYear() ? (
                              <div style={{ fontSize: '12.5px', color: 'var(--success)' }}>
                                {t('crm.bday_wished_year')} {selected.birthday_wished_year}
                                {selected.birthday_wished_by && ' ' + t('crm.by') + ' ' + selected.birthday_wished_by}
                                {selected.birthday_wished_at && ' · ' + new Date(selected.birthday_wished_at).toLocaleDateString()}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                <div style={{ fontSize: '12.5px', color: 'var(--warning)' }}>{t('crm.bday_not_wished')}</div>
                                <button onClick={() => handleSendBirthdayWish(selected)}
                                  style={{ padding: '6px 12px', borderRadius: '8px', background: 'var(--warning)', border: 'none', fontSize: '12px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', flexShrink: 0, fontFamily: 'var(--font-ui)' }}>
                                  {t('crm.bday_copy_wish')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {selected.anniversary && (
                          <div style={{ marginTop: '8px', padding: '12px 14px', borderRadius: '10px', background: selected.anniversary_wished_year === new Date().getFullYear() ? 'var(--success-light)' : PURPLE_BG, border: '1px solid ' + (selected.anniversary_wished_year === new Date().getFullYear() ? 'var(--success-border)' : PURPLE + '33') }}>
                            {selected.anniversary_wished_year === new Date().getFullYear() ? (
                              <div style={{ fontSize: '12.5px', color: 'var(--success)' }}>
                                {t('crm.anniversary_wished_year')} {selected.anniversary_wished_year}
                                {selected.anniversary_wished_by && ' ' + t('crm.by') + ' ' + selected.anniversary_wished_by}
                                {selected.anniversary_wished_at && ' · ' + new Date(selected.anniversary_wished_at).toLocaleDateString()}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                <div style={{ fontSize: '12.5px', color: PURPLE }}>{t('crm.anniversary_not_wished')}</div>
                                <button onClick={() => handleSendAnniversaryWish(selected)}
                                  style={{ padding: '6px 12px', borderRadius: '8px', background: PURPLE, border: 'none', fontSize: '12px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', flexShrink: 0, fontFamily: 'var(--font-ui)' }}>
                                  {t('crm.anniversary_copy')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Orders tab */}
              {!editMode && activeTab === 'orders' && (
                <div style={{ padding: '16px 20px' }}>
                  {customerOrders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '12.5px' }}>{t('crm.no_orders')}</div>
                  ) : customerOrders.map(order => {
                    const sc = STATUS_COLORS[order.status] || { bg: 'var(--bg-muted)', text: 'var(--text-muted)' }
                    return (
                      <div key={order.id} onClick={() => { setSelected(null); navigate('/orders/' + order.id) }}
                        style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', marginBottom: '8px' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>{order.order_code}</span>
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: sc.bg, color: sc.text }}>{order.status}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontSize: '12px', color: 'var(--text-primary)', marginBottom: '2px' }}>{order.product_name}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(order.created_at).toLocaleString('en-BD', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                            {order.order_source && <div style={{ fontSize: '10.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{t('crm.via')} {order.order_source}</div>}
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', flexShrink: 0, marginLeft: '10px', fontFamily: 'var(--font-ui)' }}>{fmt(order.total_amount)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Contact log tab */}
              {!editMode && activeTab === 'contact' && (
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ background: 'var(--bg-muted)', borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>{t('crm.log_contact_title')}</div>
                    <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
                      placeholder={t('crm.log_placeholder')} rows={3}
                      style={{ ...inp, resize: 'none', marginBottom: '8px' }}
                      onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', flexShrink: 0 }}>{t('crm.contacted_by')}</div>
                      <select value={contactBy} onChange={e => setContactBy(e.target.value)}
                        style={{ ...inp, flex: 1 }}
                        onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                        <option>Owner</option><option>Staff 1</option><option>Staff 2</option><option>Staff 3</option>
                      </select>
                      <button onClick={handleLogContact} disabled={savingNote || !newNote.trim()}
                        style={{ padding: '9px 16px', borderRadius: '9px', background: 'var(--primary)', border: 'none', fontSize: '12.5px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', flexShrink: 0, opacity: savingNote || !newNote.trim() ? 0.6 : 1, fontFamily: 'var(--font-ui)' }}>
                        {savingNote ? '...' : t('crm.log_btn')}
                      </button>
                    </div>
                  </div>
                  {customerNotes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '12.5px' }}>{t('crm.no_contacts')}</div>
                  ) : customerNotes.map(note => (
                    <div key={note.id} style={{ padding: '12px 14px', borderRadius: '10px', background: 'var(--bg-muted)', border: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: '4px' }}>{note.note}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {new Date(note.created_at).toLocaleString('en-BD', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {' · '}<strong style={{ color: 'var(--primary)' }}>{note.created_by}</strong>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteNote(note.id)}
                        style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'var(--error-light)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FlatIcon name="trash" size={12} color="var(--error)" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Timeline tab — unified activity feed */}
              {!editMode && activeTab === 'timeline' && (() => {
                const events = []
                customerOrders.forEach(o => events.push({
                  type: 'order', date: o.created_at, icon: 'plus', color: 'var(--primary)',
                  title: t('crm.timeline_order_placed'),
                  detail: (o.product_name || '') + ' · ' + fmt(o.total_amount) + ' · ' + o.order_code,
                }))
                customerNotes.forEach(n => events.push({
                  type: 'note', date: n.created_at, icon: 'chat', color: 'var(--text-muted)',
                  title: t('crm.timeline_note_logged'),
                  detail: n.note + ' — ' + n.created_by,
                }))
                if (selected.birthday_wished_at) events.push({
                  type: 'bday', date: selected.birthday_wished_at, icon: 'gift', color: 'var(--warning)',
                  title: t('crm.timeline_bday_wish'),
                  detail: selected.birthday_wished_by ? (t('crm.by') + ' ' + selected.birthday_wished_by) : '',
                })
                if (selected.anniversary_wished_at) events.push({
                  type: 'anniversary', date: selected.anniversary_wished_at, icon: 'gift', color: PURPLE,
                  title: t('crm.timeline_anniversary_wish'),
                  detail: selected.anniversary_wished_by ? (t('crm.by') + ' ' + selected.anniversary_wished_by) : '',
                })
                if (customerFlags[selected.phone]) events.push({
                  type: 'flag', date: customerFlags[selected.phone].created_at, icon: 'flag', color: 'var(--error)',
                  title: t('crm.timeline_flagged'),
                  detail: (customerFlags[selected.phone].reason_category || '').replace(/_/g, ' '),
                })
                events.push({
                  type: 'joined', date: selected.created_at, icon: 'users', color: 'var(--text-muted)',
                  title: t('crm.timeline_joined'),
                  detail: customerStats[selected.id]?.source ? (t('crm.source') + ' ' + customerStats[selected.id].source) : '',
                })
                events.sort((a, b) => new Date(b.date) - new Date(a.date))

                return (
                  <div style={{ padding: '16px 20px' }}>
                    {events.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '12.5px' }}>{t('crm.timeline_empty')}</div>
                    ) : (
                      <div style={{ position: 'relative', paddingLeft: '28px' }}>
                        <div style={{ position: 'absolute', left: '11px', top: '8px', bottom: '8px', width: '2px', background: 'var(--border)' }} />
                        {events.map((ev, i) => (
                          <div key={i} style={{ position: 'relative', marginBottom: i < events.length - 1 ? '16px' : 0 }}>
                            <div style={{ position: 'absolute', left: '-28px', top: '0', width: '22px', height: '22px', borderRadius: '50%', background: 'var(--bg-card)', border: `2px solid ${ev.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <FlatIcon name={ev.icon} size={11} color={ev.color} />
                            </div>
                            <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{ev.title}</div>
                            {ev.detail && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '1px' }}>{ev.detail}</div>}
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{new Date(ev.date).toLocaleString('en-BD', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: '16px' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '28px', maxWidth: '360px', width: '100%', fontFamily: 'var(--font-body)', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><FlatIcon name="trash" size={32} color="var(--error)" /></div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px', fontFamily: 'var(--font-ui)' }}>{t('crm.delete_title')}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: 1.6 }}>{t('crm.delete_desc')}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setDeleteId(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '9px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                {t('crm.cancel')}
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ flex: 1, padding: '11px', borderRadius: '9px', background: 'var(--error)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#FFFFFF', cursor: 'pointer', opacity: deleting ? 0.6 : 1, fontFamily: 'var(--font-ui)' }}>
                {deleting ? t('crm.deleting_btn') : t('crm.delete_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && flagTarget && (
        <FlagModal
          customerName={flagTarget.name}
          phone={flagTarget.phone}
          existingFlag={customerFlags[flagTarget.phone] || null}
          onFlag={handleFlagCustomer}
          onUnflag={handleUnflagCustomer}
          onClose={() => { setShowFlagModal(false); setFlagTarget(null) }}
        />
      )}

      <style>{`
        @keyframes crm-spin { to { transform: rotate(360deg) } }
        @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; }
        .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .pc-card-hover:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }

        .crm-desktop { display: block; }
        .crm-mobile  { display: none; }

        .crm-kpi-row { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 14px; }

        @media(max-width:1024px) {
          .crm-kpi-row       { grid-template-columns: repeat(3, 1fr); }
          .crm-chart-grid    { grid-template-columns: 1fr !important; }
          .crm-insights-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media(max-width:900px) {
          .crm-desktop     { display: none !important; }
          .crm-mobile      { display: block !important; }
          .crm-columns-btn { display: none !important; }
        }
        @media(max-width:600px) {
          .crm-insights-grid { grid-template-columns: 1fr !important; }
        }
        @media(max-width:768px) {
          .crm-kpi-row {
            display: flex; overflow-x: auto; scroll-snap-type: x mandatory;
            gap: 10px; margin: 0 -16px 14px; padding: 0 16px 4px;
            -webkit-overflow-scrolling: touch;
          }
          .crm-kpi-card { flex: 0 0 40%; scroll-snap-align: start; }
        }
        @media(max-width:600px) {
          .crm-form-grid { grid-template-columns: 1fr !important; }
          .crm-form-grid > * { grid-column: span 1 !important; }
        }
      `}</style>
    </div>
  )
}

export default CRM